import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Crown, Star, Medal, TrendingUp, Calendar, Clock, ChevronRight, Flame, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const BADGE_CONFIG = {
  legend: { icon: Crown, label: 'Legend', color: 'text-amber-500', bg: 'bg-gradient-to-r from-amber-100 to-yellow-100', border: 'border-amber-300', ring: 'ring-amber-400' },
  champion: { icon: Trophy, label: 'Social Champion', color: 'text-amber-600', bg: 'bg-gradient-to-r from-amber-50 to-orange-50', border: 'border-amber-200', ring: 'ring-amber-300' },
  rising_star: { icon: Star, label: 'Rising Star', color: 'text-indigo-500', bg: 'bg-gradient-to-r from-indigo-50 to-purple-50', border: 'border-indigo-200', ring: 'ring-indigo-300' },
};

const RANK_STYLES = {
  1: { bg: 'bg-gradient-to-r from-amber-400 to-yellow-400', text: 'text-white', shadow: 'shadow-amber-200' },
  2: { bg: 'bg-gradient-to-r from-gray-300 to-gray-400', text: 'text-white', shadow: 'shadow-gray-200' },
  3: { bg: 'bg-gradient-to-r from-amber-600 to-amber-700', text: 'text-white', shadow: 'shadow-amber-200' },
};

const ChallengeLeaderboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('weekly');
  const [weeklyData, setWeeklyData] = useState(null);
  const [allTimeData, setAllTimeData] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [weekly, allTime, tourney] = await Promise.all([
        apiClient.request('/api/advanced-engagement/leaderboard'),
        apiClient.request('/api/advanced-engagement/leaderboard/all-time'),
        apiClient.request('/api/advanced-engagement/seasonal-tournament').catch(() => null),
      ]);
      setWeeklyData(weekly);
      setAllTimeData(allTime);
      if (tourney) setTournament(tourney);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const data = tab === 'weekly' ? weeklyData : allTimeData;
  const leaderboard = data?.leaderboard || [];
  const myRank = data?.my_rank;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="challenge-leaderboard-page">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 mb-3 shadow-lg shadow-amber-200">
            <Trophy size={28} className="text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900">Challenge Leaderboard</h1>
          <p className="text-sm text-gray-500 mt-1">Top challengers earn exclusive Social Champion NFT badges</p>
        </div>

        {/* Seasonal Tournament Banner */}
        {tournament?.tournament && (
          <div className="mb-6 p-4 bg-gradient-to-r from-violet-100 via-purple-100 to-pink-100 border border-violet-200 rounded-2xl" data-testid="seasonal-tournament-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Sparkles size={22} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-black text-gray-900">{tournament.tournament.name}</p>
                  <p className="text-xs text-gray-600">
                    Complete <span className="font-bold text-violet-700">{tournament.tournament.theme}</span> challenges to earn the <span className="font-bold text-violet-700">{tournament.tournament.badge_name}</span> NFT
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-black text-violet-600">{tournament.tournament.days_remaining}</p>
                <p className="text-[10px] text-gray-400 uppercase">days left</p>
              </div>
            </div>
            {tournament.my_standing && (
              <div className="mt-3 pt-3 border-t border-violet-200 flex items-center justify-between">
                <span className="text-xs text-gray-600">Your rank: <span className="font-bold text-violet-700">#{tournament.my_standing.rank}</span></span>
                <span className="text-xs text-gray-600">{tournament.my_standing.score} challenge{tournament.my_standing.score !== 1 ? 's' : ''} completed</span>
              </div>
            )}
          </div>
        )}

        {/* Tab Switcher */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-6 max-w-xs mx-auto" data-testid="leaderboard-tabs">
          <button
            onClick={() => setTab('weekly')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === 'weekly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="leaderboard-tab-weekly"
          >
            <Calendar size={14} />
            This Week
          </button>
          <button
            onClick={() => setTab('all-time')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === 'all-time' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="leaderboard-tab-alltime"
          >
            <Clock size={14} />
            All Time
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading leaderboard...</div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-16" data-testid="leaderboard-empty">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-2xl flex items-center justify-center">
              <Medal size={32} className="text-amber-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No challengers yet</h3>
            <p className="text-sm text-gray-500 max-w-xs mx-auto">
              Complete friend challenges to claim your spot on the leaderboard!
            </p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 1 && (
              <div className="flex items-end justify-center gap-3 sm:gap-4 mb-8" data-testid="leaderboard-podium">
                {/* 2nd Place */}
                {leaderboard[1] && (
                  <div className="flex flex-col items-center w-24 sm:w-28">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full ${RANK_STYLES[2].bg} flex items-center justify-center font-black text-lg ${RANK_STYLES[2].text} shadow-md ${RANK_STYLES[2].shadow} mb-2`}>
                      2
                    </div>
                    <p className="text-xs sm:text-sm font-bold text-gray-900 truncate w-full text-center">{leaderboard[1].name?.split(' ')[0]}</p>
                    <p className="text-[10px] text-gray-400">{leaderboard[1].completed} completed</p>
                    <div className="w-full h-16 sm:h-20 bg-gray-200 rounded-t-lg mt-2" />
                  </div>
                )}

                {/* 1st Place */}
                <div className="flex flex-col items-center w-28 sm:w-32">
                  <Crown size={24} className="text-amber-400 mb-1 animate-bounce" />
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${RANK_STYLES[1].bg} flex items-center justify-center font-black text-xl ${RANK_STYLES[1].text} shadow-lg ${RANK_STYLES[1].shadow} mb-2 ring-4 ring-amber-200`}>
                    1
                  </div>
                  <p className="text-sm font-black text-gray-900 truncate w-full text-center">{leaderboard[0].name?.split(' ')[0]}</p>
                  <p className="text-[10px] text-gray-400">{leaderboard[0].completed} completed</p>
                  {leaderboard[0].badge && BADGE_CONFIG[leaderboard[0].badge] && (
                    <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${BADGE_CONFIG[leaderboard[0].badge].bg} ${BADGE_CONFIG[leaderboard[0].badge].color} border ${BADGE_CONFIG[leaderboard[0].badge].border}`}>
                      {React.createElement(BADGE_CONFIG[leaderboard[0].badge].icon, { size: 10 })}
                      {BADGE_CONFIG[leaderboard[0].badge].label}
                    </span>
                  )}
                  <div className="w-full h-24 sm:h-28 bg-gradient-to-b from-amber-300 to-amber-400 rounded-t-lg mt-2" />
                </div>

                {/* 3rd Place */}
                {leaderboard[2] && (
                  <div className="flex flex-col items-center w-24 sm:w-28">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full ${RANK_STYLES[3].bg} flex items-center justify-center font-black text-lg ${RANK_STYLES[3].text} shadow-md ${RANK_STYLES[3].shadow} mb-2`}>
                      3
                    </div>
                    <p className="text-xs sm:text-sm font-bold text-gray-900 truncate w-full text-center">{leaderboard[2].name?.split(' ')[0]}</p>
                    <p className="text-[10px] text-gray-400">{leaderboard[2].completed} completed</p>
                    <div className="w-full h-12 sm:h-14 bg-amber-700 rounded-t-lg mt-2" />
                  </div>
                )}
              </div>
            )}

            {/* Rest of Leaderboard */}
            {leaderboard.length > 3 && (
              <div className="space-y-2 mb-6" data-testid="leaderboard-list">
                {leaderboard.slice(3).map(entry => {
                  const isMe = entry.user_id === user?.user_id;
                  const badge = entry.badge ? BADGE_CONFIG[entry.badge] : null;
                  return (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        isMe
                          ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-300'
                          : 'bg-white border-gray-100 hover:border-gray-200'
                      }`}
                      data-testid={`leaderboard-row-${entry.rank}`}
                    >
                      <span className="w-8 text-center font-bold text-gray-400 text-sm">
                        {entry.rank}
                      </span>
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0">
                        {entry.name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isMe ? 'text-indigo-900' : 'text-gray-900'}`}>
                          {entry.name} {isMe && <span className="text-xs text-indigo-500">(You)</span>}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                          <span>{entry.completed} completed</span>
                          {entry.active_progress > 0 && <span>+{entry.active_progress} progress</span>}
                        </div>
                      </div>
                      {badge && (
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${badge.bg} ${badge.color} border ${badge.border}`}>
                          {React.createElement(badge.icon, { size: 10 })}
                          {badge.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* My Rank Banner (if not in top 20) */}
            {myRank && myRank.rank > 20 && (
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200 mb-6" data-testid="my-rank-banner">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold">
                    {myRank.rank}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-indigo-900">Your Rank: #{myRank.rank}</p>
                    <p className="text-xs text-indigo-600">{myRank.completed} challenges completed</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-xs text-indigo-500 font-medium">
                    <TrendingUp size={14} />
                    Keep going!
                  </div>
                </div>
              </div>
            )}

            {/* Stats Footer */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400 pt-2">
              <span className="flex items-center gap-1"><Flame size={12} /> {data?.total_participants || 0} participants</span>
              {tab === 'weekly' && weeklyData?.week_start && (
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {new Date(weeklyData.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} —{' '}
                  {new Date(weeklyData.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ChallengeLeaderboard;
